import { useMutation } from '@apollo/client'
import { message, Modal, ModalFuncProps } from 'antd'
import { useAtom } from 'jotai'
import Router, { useRouter } from 'next/router'
import { useRef } from 'react'
import {
  hasAdvanceAtom,
  initImageUrlAtom,
  referToStyleAtom,
  referToRoleAtom,
  referToStructureAtom,
} from '~/components/pages/ai/ai-form/atom'
import { setAgainParams } from '~/components/pages/ai/utils'
import { MyBtn } from '~/components/ui/MyBtn'
import { MyCheckbox } from '~/components/ui/MyCheckbox'
import {
  AI_POST_CHECK,
  AI_UPDATE_POST,
  BACK_IMAGE_POST,
  CANCEL_CREATE_AI_ARTWORK,
  COLLECT_CUSTOM_STYLE,
  DELETE_AI_ARTWORK,
  DELETE_CUSTOM_STYLE_BY_ARTWORK,
  FIX_HAND,
  PREFINE,
  SPEED_ACCELERATE_AI_ARTWORK,
  PRE_POST_BACK_IMAGE,
} from '~/graphqls/gqls'
import {
  AiArtworkAppealScene,
  AiArtworkServiceStatus,
  AiArtWorkStatus,
  aiCreateArtworkAccelerate,
  aiCreateArtworkAccelerateVariables,
  AiCreateArtworkModel,
  aiPostCheck,
  aiPostCheckVariables,
  aiUpdatePost,
  aiUpdatePostVariables,
  ArtworkStatusEnum,
  backImagePost,
  backImagePostVariables,
  cancelAiCreateArtwork,
  cancelAiCreateArtworkVariables,
  collectCustomStyle,
  collectCustomStyleVariables,
  deleteAiArtwork,
  deleteAiArtworkVariables,
  deleteCustomStyleByArtwork,
  deleteCustomStyleByArtworkVariables,
  EUploadArtWorkType,
  fixHand,
  fixHandVariables,
  getAIArtWork_pictureGraph_aiArtwork,
  getAIArtWorkNewst36_pictureGraph_aiArtworkPage_edges_node,
  prefine,
  prefineVariables,
  prePostBackImageQuery,
  prePostBackImageQueryVariables,
  ReferToScrollKeyEnum,
} from '~/graphqls/types'
import { useDebounce } from '~/hooks/useDebounce'
import {
  encrypt,
  getArtworkCopyRightStatus,
  graphQLErrorCode,
  graphQLErrorMessage,
  setLocalStorage,
  setStorage,
  xhrDownload,
} from '~/utils'
import { useAuth } from '../useAuth'
import { useImperativeQuery } from '../useImperativeQuery'
import { useStores } from '../useStores'
import { useValidateIntergal } from '../useValidateIntergal'
import { useAppeal } from './useAppeal'
import { MUTE_SCENE, useMute } from './useMute'
import { useYellow } from './useYellow'

type ProductType =
  | Omit<
      getAIArtWorkNewst36_pictureGraph_aiArtworkPage_edges_node,
      '__typename'
    >
  | getAIArtWork_pictureGraph_aiArtwork

export default function useQuickTool(product?: ProductType) {
  const { validateYellow } = useYellow(!!product?.involveYellow)
  const { verifyAppealStatus, handleAppeal } = useAppeal({
    ids: [product?.id as number],
  })
  const requestLock = useRef(false)

  const { showMute } = useMute()
  const { isAiCreator } = useAuth()
  const router = useRouter()

  const { store } = useStores()
  const [, setInitImageUrl] = useAtom(initImageUrlAtom)
  const [, setHasAdvance] = useAtom(hasAdvanceAtom)
  const [, setReferToStyle] = useAtom(referToStyleAtom)
  const [, setReferToRole] = useAtom(referToRoleAtom)
  const [, setReferToStructure] = useAtom(referToStructureAtom)

  const [deleteAction] = useMutation<deleteAiArtwork, deleteAiArtworkVariables>(
    DELETE_AI_ARTWORK
  )

  const [backImageAction] = useMutation<backImagePost, backImagePostVariables>(
    BACK_IMAGE_POST
  )

  const [speedAction] = useMutation<
    aiCreateArtworkAccelerate,
    aiCreateArtworkAccelerateVariables
  >(SPEED_ACCELERATE_AI_ARTWORK)

  const [cancelAction] = useMutation<
    cancelAiCreateArtwork,
    cancelAiCreateArtworkVariables
  >(CANCEL_CREATE_AI_ARTWORK)

  const [updatePostAction] = useMutation<aiUpdatePost, aiUpdatePostVariables>(
    AI_UPDATE_POST
  )
  const aiPostCheckAction = useImperativeQuery<
    aiPostCheck,
    aiPostCheckVariables
  >(AI_POST_CHECK)
  const aiPostCheckBackImage = useImperativeQuery<
    prePostBackImageQuery,
    prePostBackImageQueryVariables
  >(PRE_POST_BACK_IMAGE)
  const [fixHandAction] = useMutation<fixHand, fixHandVariables>(FIX_HAND)
  const { IsNotEnough, intergalModal } = useValidateIntergal()
  const [prefine] = useMutation<prefine, prefineVariables>(PREFINE)
  const [collectCustomStyleAction] = useMutation<
    collectCustomStyle,
    collectCustomStyleVariables
  >(COLLECT_CUSTOM_STYLE)
  const [deleteCustomStyleAction] = useMutation<
    deleteCustomStyleByArtwork,
    deleteCustomStyleByArtworkVariables
  >(DELETE_CUSTOM_STYLE_BY_ARTWORK)

  const validateCopyRight = (item: any, msgType?: string) => {
    const { copyRightAccept, copyRightWait } = getArtworkCopyRightStatus(item)
    let msg = copyRightAccept
      ? '该作品已上架版权市场'
      : copyRightWait
      ? '该作品版权审核中'
      : ''
    if (msgType === 'plazaRefresh') {
      msg += '，不支持更新到词/图'
    } else if (msgType === 'plazaPublish') {
      msg += '，不支持发布词/图'
    } else if (msgType === 'contribute') {
      msg += '，不支持投稿'
    } else if (msgType === 'copyrightPublish') {
      msg += '，不支持上架版权'
    }
    if (copyRightAccept || copyRightWait) {
      message.destroy()
      message.warn(msg)
      return false
    }
    return true
  }

  // 作为参考图
  const handleReferenceImage = ({ key }: { key?: string } = {}) => {
    if (product?.artWorkUrl && validateYellow()) {
      setHasAdvance(true)

      if (!key || key === ReferToScrollKeyEnum.referToImage) {
        setInitImageUrl(product.artWorkUrl)
      } else if (key === ReferToScrollKeyEnum.referToStyle) {
        setReferToStyle(pa => ({ ...pa, url: product.artWorkUrl! }))
      } else if (key === ReferToScrollKeyEnum.referToRole) {
        setReferToRole(pa => ({ ...pa, url: product.artWorkUrl! }))
      } else if (key === ReferToScrollKeyEnum.referToStructure) {
        setReferToStructure(pa => ({ ...pa, url: product.artWorkUrl! }))
      } else if (key === ReferToScrollKeyEnum.mjReferToStyle) {
        setReferToStyle(pa => ({ ...pa, url: product.artWorkUrl! }))
      } else if (key === ReferToScrollKeyEnum.mjReferToRole) {
        setReferToRole(pa => ({ ...pa, url: product.artWorkUrl! }))
      }
    }
  }

  const handleDownload = (url?: string) => {
    if (!validateYellow()) return
    if (product?.status !== AiArtWorkStatus.SUCCESS) {
      message.destroy()
      message.info('请等待作品生成后再下载')
      return
    }

    if (!url) {
      if (product.superSizePicInfo) {
        xhrDownload(product.superSizePicInfo[0]?.artWorkUrl!)
      }
      return
    }

    xhrDownload(url)
  }
  const handleArtworkSameTips = async (id: any) => {
    try {
      const res = await aiPostCheckAction({ ids: [id] })
      let canBackIamge = false
      // 判断是否能发布返图
      const resp = await aiPostCheckBackImage({ aiArtworkId: id })
      if (resp?.data?.squareModule?.prePostBackImage) {
        canBackIamge = true
      }
      let modalProps: ModalFuncProps = {
        okText: '一键返图',
        cancelText: '知道了',
        onOk: close => {
          handleBackImage(id)
          close()
        },
        onCancel: close => close(),
      }
      if (!canBackIamge) {
        modalProps = {
          okText: '知道了',
          onOk: close => {
            close()
          },
        }
      }
      if (res?.data?.squareModule?.aiPostCheck?.resCode === 1) {
        ;(canBackIamge ? Modal.confirm : Modal.info)({
          title: '提示',
          maskStyle: {
            backgroundColor: 'rgba(0,0,0,0.8)',
          },
          width: 270,
          className: 'hookModal dark',
          content: (
            <>
              <div className="!text-neutral-500 text-sm text-left">
                抱歉，为保护创作者的原创热情，一键同款而来的作品无法发布至词/图
                <br />
                您可以去APP发布广场，或一键返图，与大家一起讨论～
              </div>
            </>
          ),
          ...modalProps,
        })
        return true
      } else if (
        [2, 3, 4].includes(res?.data?.squareModule?.aiPostCheck?.resCode!)
      ) {
        let tips = ''
        switch (res?.data?.squareModule?.aiPostCheck?.resCode) {
          case 2:
            tips = '该作品的描述词原创分为0分'
            break
          case 3:
            tips = `该作品的原创分为${
              (res?.data?.squareModule?.aiPostCheck?.originalScore
                ?.originalScore || 0) / 100
            }，低于标准 1 分`
            break
          case 4:
            tips = '该作品的原创分较低'
            break
        }
        ;(canBackIamge ? Modal.confirm : Modal.info)({
          title: '提示',
          maskStyle: {
            backgroundColor: 'rgba(0,0,0,0.8)',
          },
          width: 270,
          className: 'hookModal dark',
          content: (
            <>
              <div className="!text-neutral-500 text-sm text-left">
                抱歉，{tips}
                ，无法发布至【词/图】，您可以去APP发布广场，
                {canBackIamge ? '或一键返图，' : ''}与大家一起讨论～
                <br />
                <br />
                <span className="text-[12px] text-left">
                  *原创分根据描述词丰富性&原创度，对角色同人/融合模型、高级参数、实验室工具的应用进行多维度综合评分。
                </span>
              </div>
            </>
          ),
          ...modalProps,
          // okText: '一键返图',
          // cancelText: '知道了',
          // onOk: close => {
          //   handleBackImage(id)
          //   close()
          // },
          // onCancel: close => close(),
        })
        return true
      }

      return false
    } catch (err) {
      console.error(err)
      message.error('未知错误')
      return true
    }
  }

  const handleBackImage = async (id: number) => {
    try {
      const res = await backImageAction({
        variables: {
          aiArtworkId: id,
        },
      })
      if (res.data?.squareModule?.postId) {
        message.destroy()
        message.success('发布返图成功')
        router.push(`/plaza/${res.data?.squareModule?.postId}`)
        setLocalStorage(true, 'isBackImageLocation')
      }
    } catch (err) {
      const code = graphQLErrorCode(err)
      if (code === 20063852) {
        handleAppeal(AiArtworkAppealScene.SQUARE)
        return
      }
      message.error(graphQLErrorMessage(err) || '请重试')
    }
  }

  const checkArtworkPrompt = async (data?: ProductType) => {
    const prompt = data?.prompt || ''
    const chineseRegex = /[\u4e00-\u9fa5]/g
    const chineseCount = (prompt.match(chineseRegex) || []).length
    const otherCount = prompt.length - chineseCount
    const promptCount = otherCount + chineseCount * 2

    if (promptCount < 40) {
      let canBackIamge = false
      // 判断是否能发布返图
      const resp = await aiPostCheckBackImage({ aiArtworkId: data?.id })
      if (resp?.data?.squareModule?.prePostBackImage) {
        canBackIamge = true
      }
      let modalProps: ModalFuncProps = {
        okText: '一键返图',
        cancelText: '知道了',
        onOk: close => {
          handleBackImage(data?.id)
          close()
        },
        onCancel: close => close(),
      }
      if (!canBackIamge) {
        modalProps = {
          okText: '知道了',
          onOk: close => {
            close()
          },
        }
      }

      ;(canBackIamge ? Modal.confirm : Modal.info)({
        title: '提示',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        zIndex: 10000,
        className: 'hookModal dark',
        content: (
          <>
            <div className="!text-neutral-500 text-sm text-left">
              抱歉，该作品的原创分较低，无法发布至【词/图】，您可以发布至广场，
              {canBackIamge ? '或一键返图，' : ''}与大家一起讨论~
              <br />
              <br />
              <span className="text-[12px]">
                *原创分根据描述词丰富性&原创度，对角色同人/融合模型、高级参数、实验室工具的应用进行多维度综合评分。
              </span>
            </div>
          </>
        ),

        ...modalProps,
      })
      return false
    }

    return true
  }

  const DownloadDropdown = (
    <div className="bg-neutral-200 rounded-lg text-center py-2 px-4 font-semibold text-neutral-500 shadow-[0_4px_16px_-8px_rgba(15,15,15,0.2)] min-w-[200px]">
      {product?.superSizePicInfo
        ?.filter(item => {
          return item?.multiple === 1 || !!item?.artWorkUrl
        })
        .map((item, index) => {
          return (
            <div key="index">
              {index !== 0 && (
                <div className="w-full h-px bg-neutral-300 my-2"></div>
              )}
              <div
                className="py-2 flex items-center justify-between whitespace-nowrap"
                key={item?.multiple}
              >
                {item?.multiple === 1 ? (
                  <span>原图</span>
                ) : (
                  <span>
                    <span>{`${item?.length}*${item?.width}`}</span>
                  </span>
                )}

                <span>
                  <MyBtn
                    $dark={true}
                    size="small"
                    className="ml-2.5 !border-2"
                    onClick={() => {
                      handleDownload(item?.artWorkUrl!)
                    }}
                  >
                    下载
                  </MyBtn>
                </span>
              </div>
            </div>
          )
        })}
      {product?.pretreatmentImage && (
        <div>
          <div className="w-full h-px bg-neutral-300 my-2"></div>
          <div className="py-2 flex items-center justify-between whitespace-nowrap">
            <span>
              <span>预处理图片</span>
            </span>

            <span>
              <MyBtn
                $dark={true}
                size="small"
                className="ml-2.5 !border-2"
                onClick={() => {
                  handleDownload(product.pretreatmentImage?.url)
                }}
              >
                下载
              </MyBtn>
            </span>
          </div>
        </div>
      )}
    </div>
  )

  /** 删除接口: 防抖处理 */
  const delAction = useDebounce(
    async (okCb?: () => void) => {
      try {
        await deleteAction({
          variables: {
            ids: [product?.id],
          },
        })

        okCb?.()
      } catch (err) {
        message.destroy()
        message.error(graphQLErrorMessage(err) || '请重试')
      }
    },
    [product]
  )

  return {
    handleContribute: () => {
      if (!validateCopyRight(product, 'contribute')) {
        return
      }
      if (store.userinfo?.muzzleStatus) {
        showMute(store.userinfo?.muzzleEndTime)
        return
      }
      if (!validateYellow()) return
      if (
        verifyAppealStatus({
          scene: AiArtworkAppealScene.COMPETITION,
          status: product?.riskControl?.status,
        })
      )
        return
      if (!!product?.artwork?.id) {
        if (product?.artwork?.status === ArtworkStatusEnum.ACCEPT) {
          Router.push(`/ai-competition/product/${product.artwork?.id}`)
        }
        return
      }
      if (product?.status !== AiArtWorkStatus.SUCCESS) {
        message.destroy()
        message.info('请等待作品生成后再投稿')
        return
      }
      if (product?.anyTextMode) {
        message.destroy()
        message.info('该创作暂不支持投稿')
        return
      }

      Router.push(`/ai-competition/contribution?id=${product.id}`)
    },

    handleCustom: () => {
      if (!validateYellow()) return
      Modal.info({
        centered: true,
        title: '定制商品',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <div className="text-neutral-400 text-sm -mb-2">
            请前往移动端定制商品
          </div>
        ),
        okText: '知道了',
        onOk: close => {
          close()
        },
      })
    },
    handleAgain: () => {
      if (!validateYellow()) return
      setAgainParams(product!)
    },

    handleShare: (fn?: () => void) => {
      if (!validateYellow()) return
      if (product?.status !== AiArtWorkStatus.SUCCESS) {
        message.destroy()
        message.info('请等待作品生成后再分享')
        return
      } else if (product.status === AiArtWorkStatus.SUCCESS) {
        fn?.()
      }
    },

    handleDelete: (okCb?: () => void) => {
      Modal.confirm({
        title: '删除作品',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <div className="text-neutral-400 text-sm text-center">
            确定要删除该幅作品吗?
          </div>
        ),
        okText: '确认',
        onOk: async () => {
          await delAction(okCb)
        },
        cancelText: '取消',
        onCancel: close => {
          close()
        },
      })
    },
    handleSpeedCreate: ({
      points,
      vipTimes,
    }: {
      points: number
      vipTimes: number
    }) => {
      if (requestLock.current) return
      requestLock.current = true
      return speedAction({
        variables: {
          param: {
            id: product?.id,
            integralNum: 0,
            payStrategy: {
              integralUse: {
                integralUseOnAccelerate: points,
              },
              vipRightsUse: {
                accelerateTimesUse: vipTimes,
              },
            },
          },
        },
      })
        .then(res => {
          if (res?.data?.pictureMutation?.status) {
            message.destroy()
            message.success('加速成功')
          }
        })
        .catch(err => {
          message.destroy()
          message.error(graphQLErrorMessage(err) || '加速失败')
        })
        .finally(() => {
          requestLock.current = false
        })
    },
    handleCancelCreate: (okCb?: () => void) => {
      Modal.confirm({
        title: '撤销',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <div className="text-neutral-400 text-sm text-center">
            确定要撤销作品生成吗？
          </div>
        ),
        okText: '确认',
        onOk: close => {
          if (requestLock.current) return
          requestLock.current = true
          cancelAction({
            variables: {
              id: product?.id,
            },
          })
            .then(res => {
              if (res?.data?.pictureMutation?.status) {
                okCb?.()
              }
            })
            .catch(err => {
              message.destroy()
              message.error(graphQLErrorMessage(err) || '撤销失败')
            })
            .finally(() => {
              requestLock.current = false
            })
          close()
        },
        onCancel: close => {
          close()
        },
      })
    },
    DownloadDropdown,
    hiddenDownloadDropdown:
      product?.superSizePicInfo?.filter(item => {
        return item?.multiple === 1 || !!item?.artWorkUrl
      }).length === 1 && !product.pretreatmentImage,
    handleDownload,
    handleReferenceImage,
    /** 神奇涂抹 */
    handleMagic: () => {
      if (!validateYellow()) return
      setStorage('aiParam', {
        id: product?.id,
        model: AiCreateArtworkModel.STABLE_DIFFUSION,
        originUrl:
          product?.serviceStatus === AiArtworkServiceStatus.CLEANED
            ? ''
            : product?.miniArtWorkUrl,
      })
      Router.push('/lab/paint')
    },
    handlePlazaPublish: async () => {
      if (store.userinfo?.muzzleStatus) {
        showMute(store.userinfo?.muzzleEndTime, MUTE_SCENE.SQUARE)
        return
      }
      if (
        verifyAppealStatus({
          status: product?.riskControl?.status,
          scene: AiArtworkAppealScene.SQUARE,
        })
      ) {
        return
      }
      if (!validateCopyRight(product, 'plazaPublish')) {
        return
      }
      const checkPrompt = await checkArtworkPrompt(product)
      if (!checkPrompt) {
        return
      }
      const shouldInterupt = await handleArtworkSameTips(product?.id)
      if (!shouldInterupt) {
        Router.push(`/plaza/publish?ids=${product?.id}&fourGrid=true`)
      }
    },
    /** 上架版权市场 */
    handleCopyrightPublish: (type?: EUploadArtWorkType) => {
      // 需ai创作者
      if (!isAiCreator) {
        Modal.confirm({
          title: '提示',
          maskStyle: {
            backgroundColor: 'rgba(0,0,0,0.8)',
          },
          width: 270,
          className: 'hookModal dark',
          content: <>您需要成为AI创作者，才能申请上架版权哦～</>,
          okText: '前往申请',
          onOk: () => {
            window.open(
              `${process.env.NEXT_PUBLIC_WUJIEBANTU_HOST}/to-be-creator`
            )
          },
          cancelText: '取消',
          onCancel: close => close(),
        })
        return
      }
      // 申诉状态 校验
      if (
        verifyAppealStatus({
          status: product?.riskControl?.status,
          scene: AiArtworkAppealScene.SQUARE,
        })
      ) {
        return
      }
      // 上架中或者已上架版权市场 校验
      if (!validateCopyRight(product, 'copyrightPublish')) {
        return
      }
      // id参数进行AES对称加密，防止用户随意的替换ID参数
      window.open(
        `${
          process.env.NEXT_PUBLIC_WUJIEBANTU_HOST
        }/upload-product?param=${encrypt(
          JSON.stringify({
            id: product?.id,
          }),
          'COPYRIGHTPUBLISH'
        )}`
      )
    },
    handlePlazaRefresh: (postId: number, okCb?: () => void) => {
      if (!validateCopyRight(product, 'plazaRefresh')) {
        return
      }
      if (store.userinfo?.muzzleStatus) {
        showMute(store.userinfo?.muzzleEndTime, MUTE_SCENE.SQUARE)
        return
      }
      if (
        verifyAppealStatus({
          status: product?.riskControl?.status,
          scene: AiArtworkAppealScene.SQUARE,
        })
      )
        return
      Modal.confirm({
        title: '提示',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: 'hookModal dark',
        content: (
          <div className="text-neutral-400 text-sm text-left -mb-2">
            相同的描述及创作参数，将以多张图片的形式更新至已发布的内容。多图展示将被更多推荐哦～
          </div>
        ),
        okText: '确认更新',
        onOk: async close => {
          if (requestLock.current) return
          try {
            requestLock.current = true
            await updatePostAction({
              variables: {
                input: {
                  aiArtworkId: product?.id,
                  postId,
                },
              },
            })
            requestLock.current = false
            okCb?.()
          } catch (err) {
            const code = graphQLErrorCode(err)
            if (code === 20063852) {
              handleAppeal(AiArtworkAppealScene.SQUARE)
              return
            }
            message.destroy()
            message.error(graphQLErrorMessage(err) || '请重试')
            requestLock.current = false
          }
          close()
        },
        cancelText: '取消',
        onCancel: close => {
          close()
        },
      })
    },
    handleFixHand: async () => {
      const fixHand = async () => {
        if (requestLock.current) return
        try {
          const res = await fixHandAction({
            variables: {
              id: product?.id,
            },
          })
          const id = res.data?.pictureMutation?.id
          if (id) {
            Router.push(`/ai/${id}`)
          }
        } catch (err) {
          /** 积分不足 */
          if (IsNotEnough(err)) {
            intergalModal()
          } else {
            message.destroy()
            message.error(graphQLErrorMessage(err) || '请重试')
          }
        } finally {
          requestLock.current = false
        }
      }
      const shouldPrompt =
        localStorage.getItem('_hide_fix_hand_tips') !== 'true'
      if (shouldPrompt) {
        Modal.confirm({
          title: '提示',
          maskStyle: {
            backgroundColor: 'rgba(0,0,0,0.8)',
          },
          width: 270,
          className: 'hookModal dark',
          content: (
            <div>
              <div className="text-neutral-400 text-sm text-left">
                修手功能会基于原图结构重新生成一张优化手部的图片，此功能将消耗2积分（作画生成），原图尺寸若额外使用了积分，修手将消耗同等积分。
              </div>
              <MyCheckbox
                className="customCheckbox"
                $dark
                onClick={e => {
                  localStorage.setItem(
                    '_hide_fix_hand_tips',
                    (e.target as any).checked
                  )
                }}
              >
                不再提示
              </MyCheckbox>
            </div>
          ),
          okText: '去生成',
          onOk: async close => {
            await fixHand()
          },
          onCancel: close => {
            close()
          },
        })
      } else {
        await fixHand()
      }
    },

    handlePrefine: async () => {
      if (!validateYellow()) return
      const onPrefine = async () => {
        if (requestLock.current) return
        try {
          const res = await prefine({
            variables: {
              id: product?.id,
            },
          })
          const id = res.data?.pictureMutation?.id
          if (id) {
            Router.push(`/ai/${id}`)
          }
        } catch (err) {
          /** 积分不足 */
          if (IsNotEnough(err)) {
            intergalModal()
          } else {
            message.destroy()
            message.error(graphQLErrorMessage(err) || '请重试')
          }
        } finally {
          requestLock.current = false
        }
      }

      const shouldPrompt = localStorage.getItem('_hide_prefine_tips') !== 'true'

      if (shouldPrompt) {
        Modal.confirm({
          title: '提示',
          maskStyle: {
            backgroundColor: 'rgba(0,0,0,0.8)',
          },
          width: 270,
          className: 'hookModal dark',
          content: (
            <div>
              <div className="text-neutral-400 text-sm text-left">
                精绘功能将采用原创作参数新生成一张精绘图片，此功能将消耗2积分（作画生成），以及额外的精绘积分。
              </div>
              <MyCheckbox
                className="customCheckbox"
                $dark
                onClick={e => {
                  localStorage.setItem(
                    '_hide_prefine_tips',
                    (e.target as any).checked
                  )
                }}
              >
                不再提示
              </MyCheckbox>
            </div>
          ),
          okText: '去生成',
          onOk: async close => {
            await onPrefine()
          },
          onCancel: close => {
            close()
          },
        })
      } else {
        await onPrefine()
      }
    },
    handleArtworkSameTips,

    /** 收藏自定义风格模型 */
    handleCollectCustomStyle: async (name: string) => {
      try {
        const res = await collectCustomStyleAction({
          variables: {
            id: product?.id,
            name: name,
          },
        })
        message.destroy()
        message.success('收藏成功')
        return true
      } catch (err) {
        message.destroy()
        message.error(graphQLErrorMessage(err || '收藏失败'))
        return false
      }
    },

    /** 删除收藏的自定义风格模型 */
    handleDeleteCustomStyle: async () => {
      try {
        const res = await deleteCustomStyleAction({
          variables: {
            id: product?.id,
          },
        })
      } catch (err) {
        message.destroy()
        message.error(graphQLErrorMessage(err || '收藏失败'))
      }
    },
    /** 查看返图 */
    handleViewBackImage: () => {
      setLocalStorage(true, 'isBackImageLocation')
      router.push(`/plaza/${product?.extend?.backImagePostId}`)
    },
    /** 校验是否描述词低于40 */
    checkArtworkPrompt,
  }
}
